import { FC, useCallback, useRef, DragEvent } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  useReactFlow,
  Connection,
  NodeChange,
  EdgeChange,
  IsValidConnection,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { nodeTypes } from './nodes/nodeTypes'
import { edgeTypes } from './edges/edgeTypes'
import { WORKFLOW } from './tokens'
import { Node, Edge } from '@xyflow/react'
import { SidebarDragData } from './types'

interface WorkflowCanvasProps {
  nodes: Node[]
  edges: Edge[]
  onNodesChange: (changes: NodeChange[]) => void
  onEdgesChange: (changes: EdgeChange[]) => void
  onConnect: (connection: Connection) => void
  onNodeClick: (nodeId: string) => void
  onPaneClick: () => void
  onDrop: (position: { x: number; y: number }, dragData: SidebarDragData) => void
  selectedNodeId: string | null
}

const WorkflowCanvas: FC<WorkflowCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onPaneClick,
  onDrop,
  selectedNodeId,
}) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const { screenToFlowPosition } = useReactFlow()

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      const raw = event.dataTransfer.getData('application/workflow-node')
      if (!raw) return

      const dragData = JSON.parse(raw) as SidebarDragData
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      onDrop(position, dragData)
    },
    [screenToFlowPosition, onDrop],
  )

  const isValidConnection: IsValidConnection = useCallback(
    (connection) => {
      if (connection.source === connection.target) return false
      const targetNode = nodes.find((n) => n.id === connection.target)
      if (targetNode?.type === 'trigger') return false
      return true
    },
    [nodes],
  )

  const styledNodes = nodes.map((node) => ({
    ...node,
    selected: node.id === selectedNodeId,
  }))

  return (
    <div ref={reactFlowWrapper} style={{ flex: 1, height: '100%' }}>
      <ReactFlow
        nodes={styledNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={(_event, node) => onNodeClick(node.id)}
        onPaneClick={onPaneClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{ type: 'workflow', animated: true }}
        isValidConnection={isValidConnection}
        fitView
        snapToGrid
        snapGrid={[WORKFLOW.canvas.gridSize, WORKFLOW.canvas.gridSize]}
        proOptions={{ hideAttribution: true }}
        deleteKeyCode={['Delete', 'Backspace']}
        style={{ background: WORKFLOW.canvas.bg }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={WORKFLOW.canvas.gridSize}
          size={1}
          color={WORKFLOW.canvas.gridColor}
        />
        <Controls
          showInteractive={false}
          style={{ borderRadius: 10, overflow: 'hidden' }}
        />
        <MiniMap
          nodeStrokeWidth={3}
          pannable
          zoomable
          style={{
            borderRadius: 10,
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        />
      </ReactFlow>
    </div>
  )
}

export default WorkflowCanvas
